import type { ApiResponse } from '@/shared/types';
import api from '@/shared/api/client';
import { i18n } from '@/i18n';

export const operatorSettingsApi = {
  getSummary: () => api.get<ApiResponse<any>>('/operator/summary'),

  getPlatformSettings: () =>
    api.get<ApiResponse<any>>('/operator/platform-settings'),

  updatePlatformSettings: (data: {
    feedbackNotifyEmail?: string;
    auditLogRetentionDays?: number;
    apiRequestLogRetentionDays?: number;
    reportLogRetentionDays?: number;
    batchAuditLogRetentionDays?: number;
    webhookLogRetentionDays?: number;
    emailLogRetentionDays?: number;
    aiCallLogRetentionDays?: number;
    logRetentionArchiveEnabled?: boolean;
  }) => api.patch<ApiResponse<any>>('/operator/platform-settings', data),

  listBackups: () => api.get<ApiResponse<any[]>>('/operator/backups'),

  runBackup: () => api.post<ApiResponse<any>>('/operator/backups/run'),

  runRestoreDrill: (backupId: string) =>
    api.post<ApiResponse<any>>(`/operator/backups/${backupId}/restore-drill`),

  restoreBackup: (backupId: string) =>
    api.post<ApiResponse<any>>(
      `/operator/backups/${backupId}/restore`,
      undefined,
      {
        timeout: 600000,
      },
    ),

  downloadBackup: async (
    backupId: string,
    fallbackName: string,
    format: 'encrypted' | 'decrypted' = 'encrypted',
  ) => {
    const response = await api.get(`/operator/backups/${backupId}/download`, {
      params: { format },
      responseType: 'blob',
      timeout: 300000,
    });
    const blob = response.data as Blob;
    if (blob.type.includes('application/json')) {
      const text = await blob.text();
      const payload = JSON.parse(text) as { error?: string; message?: string };
      throw new Error(
        payload.error ||
          payload.message ||
          i18n.global.t('operatorBackups.messages.downloadFailed'),
      );
    }
    const disposition = response.headers['content-disposition'] as
      | string
      | undefined;
    const matched = disposition?.match(/filename="?([^"]+)"?/i);
    const filename = matched?.[1] || fallbackName;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },

  deleteBackup: (backupId: string) =>
    api.delete<ApiResponse<any>>(`/operator/backups/${backupId}`),

  listDeletionRequests: () =>
    api.get<ApiResponse<any[]>>('/operator/data-deletions'),

  retryDeletionRequest: (id: string) =>
    api.post<ApiResponse<any>>(`/operator/data-deletions/${id}/retry`),

  cancelDeletionRequest: (id: string) =>
    api.post<ApiResponse<any>>(`/operator/data-deletions/${id}/cancel`),

  executeDeletionNow: (id: string) =>
    api.post<ApiResponse<any>>(`/operator/data-deletions/${id}/execute`),

  getLineChannelBindingStatus: () =>
    api.get<ApiResponse<{
      configured: boolean;
      environment: string;
      webhookUrl: string;
      envStatus: {
        lineChannelId: boolean;
        lineChannelSecret: boolean;
        lineChannelAccessToken: boolean;
        lineBotDestination: boolean;
      };
      binding: {
        id: string;
        status: string;
        destinationMasked: string;
        channelIdReference: string;
        webhookEnabled: boolean;
        inboundEnabled: boolean;
        lastWebhookAt: string | null;
        updatedAt: string;
      } | null;
    }>>('/operator/line-channel-binding'),

  bootstrapLineChannelBinding: () =>
    api.post<ApiResponse<{
      configured: boolean;
      environment: string;
      bindingId: string;
      destinationMasked: string;
      channelIdMasked: string;
      officialAccount: { displayName: string | null; basicId: string | null };
      idempotent: boolean;
    }>>('/operator/line-channel-binding/bootstrap'),
};
